import { useEffect, useState } from "react";
import { api, formatErr, inr } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Users } from "lucide-react";
import { toast } from "sonner";
import { PaginationBar, usePagination } from "@/components/Pagination";

export default function CustomerLedger() {
  const [customers, setCustomers] = useState([]);
  const [cid, setCid] = useState("");
  const [data, setData] = useState(null);
  const pg = usePagination(data?.rows || [], data);

  useEffect(() => { api.get("/customers").then(r => setCustomers(r.data)).catch(e => toast.error(formatErr(e))); }, []);
  useEffect(() => {
    if (!cid) { setData(null); return; }
    api.get(`/reports/customer-ledger/${cid}`).then(r => setData(r.data)).catch(e => toast.error(formatErr(e)));
  }, [cid]);

  return (
    <div className="p-8">
      <div className="mb-6">
        <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><Users className="w-3 h-3"/> Accounts · Sales & receipts per customer</div>
        <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Customer Ledger</h1>
      </div>

      <div className="mb-4 max-w-md">
        <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Customer</Label>
        <Select value={cid} onValueChange={setCid}>
          <SelectTrigger data-testid="select-customer" className="mt-1 rounded-sm bg-secondary border-border"><SelectValue placeholder="Pick a customer"/></SelectTrigger>
          <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name} · {c.phone}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-3 gap-3 mb-6">
            <div className="border border-border bg-card rounded-sm p-3"><div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Total Sales (Dr)</div><div className="font-display font-black text-xl mt-1">{inr(data.total_dr)}</div></div>
            <div className="border border-border bg-card rounded-sm p-3"><div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Total Receipts (Cr)</div><div className="font-display font-black text-xl mt-1 text-green-500">{inr(data.total_cr)}</div></div>
            <div className="border border-primary/40 bg-primary/5 rounded-sm p-3"><div className="text-[10px] uppercase tracking-[0.2em] text-primary">Outstanding Due</div><div className={`font-display font-black text-xl mt-1 ${data.closing_balance > 0 ? "text-destructive" : "text-primary"}`} data-testid="cust-outstanding">{inr(data.closing_balance)}</div></div>
          </div>
          <div className="border border-border bg-card rounded-sm overflow-hidden overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                <tr><th className="text-left p-3">Date</th><th className="text-left p-3">Ref #</th><th className="text-left p-3">Particulars</th><th className="text-right p-3">Debit</th><th className="text-right p-3">Credit</th><th className="text-right p-3">Balance</th></tr>
              </thead>
              <tbody>
                {data.rows.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No entries for this customer</td></tr>}
                {pg.pageItems.map((r, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="p-3 font-mono text-xs">{r.date}</td>
                    <td className="p-3 font-mono text-xs text-primary uppercase">{r.ref}</td>
                    <td className="p-3 text-xs">{r.particulars}</td>
                    <td className="p-3 text-right font-mono text-xs">{r.dr ? inr(r.dr) : "-"}</td>
                    <td className="p-3 text-right font-mono text-xs text-green-500">{r.cr ? inr(r.cr) : "-"}</td>
                    <td className="p-3 text-right font-mono">{inr(r.balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <PaginationBar pg={pg}/>
          </div>
        </>
      )}
    </div>
  );
}
